import Chip, { type ChipProps } from '@mui/material/Chip';
import SvgIcon from '@mui/material/SvgIcon';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { parseISO, startOfDay } from 'date-fns';
import { pick, startCase } from 'lodash-es';
import type { IntlShape } from 'react-intl';
import { Model } from 'survey-core';
import { SupplierRisksIcons } from './components/SupplierRiskIcon';
import {
   classifySupplyChainRiskScore,
   DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE,
   getSupplyChainRiskBandDefinition,
   type SupplyChainRiskClassificationScale,
} from './riskClassification';
import {
   type CaseEvaluationComputedScore,
   type CaseEvaluationFactors,
   type CaseEvaluationItem,
   type CaseEvaluationStatus,
   type CaseRiskEvaluation,
   type LocalizedString,
   type QuestionnaireAnswerImpactEntry,
   type QuestionnaireAnswerRow,
   type QuestionnaireOverviewStatus,
   type Supplier,
   type SupplierAddress,
   type SupplierCase,
   type SupplierOption,
   type SupplierOrganisationPeriod,
   type SupplierRiskScore,
   surveyJsonSchema,
} from './types';
import {
   type ActionStatusEnum,
   CaseStatus,
   type CaseStatusEnum,
   RiskLevels,
   type RiskLevelsEnum,
   type RisksEnum,
   SupplyChainPriority,
   type SupplyChainPriorityEnum,
   supplyChainRiskCategoryValues,
} from './types/enums';
import { type SupplyChainQuestionnaire, type User } from 'types/models';

const isAsciiAlpha = (value: string): boolean => {
   if (value.length !== 2) return false;
   const first = value.charCodeAt(0);
   const second = value.charCodeAt(1);
   const isLetter = (code: number): boolean => (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
   return isLetter(first) && isLetter(second);
};

export const findLocaleFromUrl = (search?: string) => {
   const resolvedSearch = search ?? (typeof window !== 'undefined' ? window.location.search : '');

   if (!resolvedSearch) return null;

   const params = new URLSearchParams(resolvedSearch);
   const raw = params.get('lang');

   if (!raw) return null;

   const trimmed = raw.trim();

   if (!trimmed) return null;

   const parts = trimmed.split('-');

   if (parts.length > 2) return null;

   const [language, region] = parts;

   if (!language || !isAsciiAlpha(language)) return null;

   if (region && !isAsciiAlpha(region)) return null;

   if (!region) return language.toLowerCase();

   return `${language.toLowerCase()}-${region.toUpperCase()}`;
};

const questionnaireSurveyLogoPropertyNames = ['logo', 'logoPosition', 'logoWidth', 'logoHeight', 'logoFit'] as const;

export const isQuestionnaireSurveyLogoProperty = (propertyName: string) =>
   questionnaireSurveyLogoPropertyNames.some((logoPropertyName) => logoPropertyName === propertyName);

export const stripQuestionnaireSurveyLogo = <T>(surveyJson: T): T => {
   if (!surveyJson || typeof surveyJson !== 'object' || Array.isArray(surveyJson)) return surveyJson;

   const currentSurveyJson = surveyJson as Record<string, unknown>;
   if (!questionnaireSurveyLogoPropertyNames.some((propertyName) => propertyName in currentSurveyJson)) return surveyJson;

   const nextSurveyJson = { ...currentSurveyJson };
   questionnaireSurveyLogoPropertyNames.forEach((propertyName) => {
      delete nextSurveyJson[propertyName];
   });

   return nextSurveyJson as T;
};

type ChipOrTypographyOrSvgIcon<T extends typeof Chip | typeof Typography | typeof SvgIcon> = T extends typeof Chip
   ? ChipProps['color']
   : T extends typeof Typography
     ? TypographyProps['color']
     : string;

/**
 * Returns the color for a risk category value, depending on the MUI component type.
 */
export function getRiskCategoryColor<T extends typeof Chip | typeof Typography | typeof SvgIcon>(
   value: number | undefined,
   type: T,
   scale: SupplyChainRiskClassificationScale = DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE
): ChipOrTypographyOrSvgIcon<T> {
   const band = classifySupplyChainRiskScore(value, scale);
   const muiColor = band ? getSupplyChainRiskBandDefinition(band).muiColor : null;

   if (type === Chip) {
      return (muiColor ?? 'default') as keyof ChipProps['color'];
   }
   if (type === SvgIcon) {
      return (muiColor ? `${muiColor}.main` : 'text.secondary') as ChipOrTypographyOrSvgIcon<T>;
   }
   if (type === Typography) {
      return (muiColor ?? 'textPrimary') as keyof TypographyProps['color'];
   }
   return 'success' as ChipOrTypographyOrSvgIcon<T>;
}

export const deriveRiskLevel = (
   value: number,
   scale: SupplyChainRiskClassificationScale = DEFAULT_SUPPLY_CHAIN_RISK_CLASSIFICATION_SCALE
): RiskLevelsEnum | null => {
   const band = classifySupplyChainRiskScore(value, scale);
   if (band === 'HIGH') return RiskLevels.HIGH;
   if (band === 'MEDIUM') return RiskLevels.MEDIUM;
   if (band === 'LOW') return RiskLevels.LOW;
   return null;
};

export const getCasesAndActionsStatusColor = (value: CaseStatusEnum | ActionStatusEnum) => {
   if (value === CaseStatus.NEW) return 'success.main';
   if (value === CaseStatus.OPEN) return 'info.main';
   if (value === CaseStatus.IN_PROGRESS) return 'warning.main';
   if (value === CaseStatus.CLOSED) return 'text.disabled';
   return 'text.secondary';
};

export const getCasesAndActionsStatusLabelId = (value: CaseStatusEnum | ActionStatusEnum) => {
   const map: Record<string, string> = {
      [CaseStatus.NEW]: 'supplyChain.generic.casesAndActionsStatus.new',
      [CaseStatus.OPEN]: 'supplyChain.generic.casesAndActionsStatus.open',
      [CaseStatus.IN_PROGRESS]: 'supplyChain.generic.casesAndActionsStatus.inProgress',
      [CaseStatus.CLOSED]: 'supplyChain.generic.casesAndActionsStatus.closed',
   };
   return map[value] ?? value;
};

export const getPriorityColor = (value: SupplyChainPriorityEnum): ChipProps['color'] => {
   if (value === SupplyChainPriority.HIGH) return 'error';
   if (value === SupplyChainPriority.MEDIUM) return 'warning';
   return 'success';
};

export const getPriorityLabelId = (value: SupplyChainPriorityEnum) => {
   const map: Record<string, string> = {
      [SupplyChainPriority.LOW]: 'supplyChain.generic.priority.low',
      [SupplyChainPriority.MEDIUM]: 'supplyChain.generic.priority.medium',
      [SupplyChainPriority.HIGH]: 'supplyChain.generic.priority.high',
   };
   return map[value] ?? value;
};

export type LatitudeLongitude = [latitude: number, longitude: number];

export const getSupplierCoordinates = (address?: SupplierAddress | null): LatitudeLongitude | null => {
   if (!Array.isArray(address?.geography?.coordinates)) return null;
   if (address.geography.coordinates.length !== 2) return null;

   const [longitude, latitude] = address.geography.coordinates;

   if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

   return [latitude, longitude];
};

export const caseEvaluationFactorKeys = ['magnitude', 'scope', 'irreversibility', 'probability'] as const;
const caseEvaluationFactorMin = 1;
const caseEvaluationFactorMax = 5;
const caseEvaluationProbabilityMin = 1;
const caseEvaluationProbabilityMax = 100;
const caseEvaluationScoreMin = 1;

const hasFactorValue = (value?: number) => value !== undefined && value != null;

const isFactorInRange = (value?: number): value is number =>
   hasFactorValue(value) && value >= caseEvaluationFactorMin && value <= caseEvaluationFactorMax;

const isProbabilityInRange = (value?: number): value is number =>
   hasFactorValue(value) && value >= caseEvaluationProbabilityMin && value <= caseEvaluationProbabilityMax;

const hasAnyEvaluationFactor = (factors: CaseEvaluationFactors = {}) => caseEvaluationFactorKeys.some((key) => hasFactorValue(factors[key]));

const hasCompleteEvaluationFactors = (factors: CaseEvaluationFactors = {}) =>
   isFactorInRange(factors.magnitude) &&
   isFactorInRange(factors.scope) &&
   isFactorInRange(factors.irreversibility) &&
   isProbabilityInRange(factors.probability);

const _getCaseEvaluationStatus = (factors: CaseEvaluationFactors = {}, closed = false): CaseEvaluationStatus => {
   if (!hasAnyEvaluationFactor(factors)) return 'OPEN';
   if (closed && hasCompleteEvaluationFactors(factors)) return 'CLOSED';
   return 'IN_PROGRESS';
};

const calculateSeverityScore = (factors: CaseEvaluationFactors = {}) => {
   if (!isFactorInRange(factors.magnitude) || !isFactorInRange(factors.scope) || !isFactorInRange(factors.irreversibility)) return null;
   return (factors.magnitude + factors.scope + factors.irreversibility) / 3;
};

export const calculateCaseEvaluationScore = (factors: CaseEvaluationFactors = {}): CaseEvaluationComputedScore | null => {
   const severity = calculateSeverityScore(factors);
   if (severity === null || !isProbabilityInRange(factors.probability)) return null;

   const evaluationScore = Math.max(caseEvaluationScoreMin, severity * (factors.probability / 100));
   return { severity, evaluationScore };
};

const supplyChainRiskCategoryValueSet = new Set<string>(supplyChainRiskCategoryValues);

export const isSupplyChainRiskCategory = (value: unknown): value is RisksEnum =>
   typeof value === 'string' && supplyChainRiskCategoryValueSet.has(value);

const findSupplyChainRiskCategory = (value: unknown) => {
   if (typeof value !== 'string') return null;
   return supplyChainRiskCategoryValues.find((riskCategory) => riskCategory === value) ?? null;
};

const normalizeRiskCategoryList = (value: string | RisksEnum[]): RisksEnum[] => {
   if (Array.isArray(value)) {
      return value.reduce<RisksEnum[]>((acc, entry) => {
         const riskCategory = findSupplyChainRiskCategory(entry);
         if (riskCategory) acc.push(riskCategory);
         return acc;
      }, []);
   }

   const riskCategory = findSupplyChainRiskCategory(value);
   if (riskCategory) return [riskCategory];

   return [];
};

export const normalizeRiskCategories = (value: string | RisksEnum[]): RisksEnum[] => {
   return normalizeRiskCategoryList(value);
};

export const normalizeRiskCategoryKey = (value: string): RisksEnum | '' => {
   const trimmed = value.trim();
   if (!trimmed) return '';
   const normalized = trimmed.toUpperCase();
   const riskCategory = findSupplyChainRiskCategory(normalized);

   return riskCategory ?? '';
};

const averagePresentScores = (values: Array<number | null | undefined>): number | null => {
   const present = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
   if (present.length === 0) return null;
   return present.reduce((sum, value) => sum + value, 0) / present.length;
};

export const resolveCurrentCategoryScore = (
   score: Pick<SupplierRiskScore, 'categoryScores' | 'sectorCategoryScores' | 'concreteCategoryScores'> | null | undefined,
   slug: string
): number | null => {
   if (!score) return null;
   const key = normalizeRiskCategoryKey(slug);
   const concrete = score.concreteCategoryScores?.[key];
   if (concrete != null) return concrete;
   return averagePresentScores([score.categoryScores?.[key], score.sectorCategoryScores?.[key]]);
};

export const buildRiskCategoryLabel = (riskCategoryKey: string) => startCase(riskCategoryKey.toLowerCase().replaceAll('_', ' '));

const hasIntlMessage = (intl: IntlShape, id: string) => Object.prototype.hasOwnProperty.call(intl.messages, id);

export const resolveRiskCategoryLabel = (intl: IntlShape, riskCategoryKey: string) => {
   const normalizedRiskCategoryKey = normalizeRiskCategoryKey(riskCategoryKey);
   if (!normalizedRiskCategoryKey) return '';

   const supplierRiskMessageId = `supplyChain.risksEnum.${normalizedRiskCategoryKey}`;
   if (hasIntlMessage(intl, supplierRiskMessageId)) {
      return intl.formatMessage({
         id: supplierRiskMessageId,
         defaultMessage: buildRiskCategoryLabel(normalizedRiskCategoryKey),
      });
   }

   return buildRiskCategoryLabel(normalizedRiskCategoryKey);
};

const listCaseRiskCategoryKeys = (supplierCase?: Partial<SupplierCase> | null): RisksEnum[] => {
   if (!supplierCase) return [];

   const keys = normalizeRiskCategories(supplierCase.riskCategory ?? []);
   return Array.from(new Set(keys));
};

const getEvaluationByCategory = (supplierCase?: Partial<SupplierCase> | null) => {
   if (!supplierCase?.evaluations?.length) return {};

   return supplierCase.evaluations.reduce((acc: Record<string, any>, item: { riskCategory: string | undefined }) => {
      if (item.riskCategory) {
         const key = normalizeRiskCategoryKey(item.riskCategory);
         if (!key) return acc;
         acc[key] = item;
      }
      return acc;
   }, {});
};

const buildEvaluationState = (supplierCase?: Partial<SupplierCase> | null) => {
   if (!supplierCase?.evaluationState) return {};

   return Object.values(supplierCase.evaluationState).reduce<Record<string, CaseRiskEvaluation>>((acc, evaluation) => {
      if (evaluation?.riskCategoryKey) {
         const key = normalizeRiskCategoryKey(evaluation.riskCategoryKey);
         if (!key) return acc;

         acc[key] = {
            ...evaluation,
            riskCategoryKey: key,
         };
      }
      return acc;
   }, {});
};

export const buildCaseEvaluationItems = (supplierCase?: Partial<SupplierCase> | null): CaseEvaluationItem[] => {
   if (!supplierCase) return [];

   const categories = listCaseRiskCategoryKeys(supplierCase);
   const evaluationStateByCategory = buildEvaluationState(supplierCase);
   const evaluationByCategory = getEvaluationByCategory(supplierCase);
   const categorySet = new Set<string>([...categories, ...Object.keys(evaluationStateByCategory), ...Object.keys(evaluationByCategory)]);

   const out = Array.from(categorySet, (riskCategoryKey, index) => {
      const currentEvaluation = evaluationStateByCategory[riskCategoryKey];
      const initialEvaluation = evaluationByCategory[riskCategoryKey];
      const currentRiskScore = currentEvaluation?.currentRiskScore ?? initialEvaluation?.value;
      const updatedRiskScore = currentEvaluation?.updatedRiskScore;
      // const value = updatedRiskScore ?? currentRiskScore;

      return {
         id: initialEvaluation?.id ?? index + 1,
         slug: riskCategoryKey,
         label: buildRiskCategoryLabel(riskCategoryKey),
         value: initialEvaluation?.evaluationScore ?? null,
         currentRiskScore,
         updatedRiskScore,
         evaluationScore: initialEvaluation?.evaluationScore,
         status: currentEvaluation?.status ?? initialEvaluation?.status ?? 'OPEN',
         updatedAt: currentEvaluation?.updatedAt ?? initialEvaluation?.updatedAt,
         factors: { ...pick(initialEvaluation, ['probability', 'magnitude', 'irreversibility', 'scope']) },
      };
   });

   return out.sort((left, right) => String(left.label).localeCompare(String(right.label)));
};

const _mergeEvaluationFactors = (base: CaseEvaluationFactors = {}, next: CaseEvaluationFactors = {}): CaseEvaluationFactors => ({
   ...base,
   ...next,
});

interface SummaryCategoryScore {
   key: string;
   value: number;
   updatedAtMs: number;
}

const toEpochMs = (value?: string | Date) => {
   if (!value) return 0;
   const epochMs = new Date(value).getTime();
   return Number.isFinite(epochMs) ? epochMs : 0;
};

export const getSupplierCaseEvaluationSummary = (cases: SupplierCase[] = [], supplierId?: number) => {
   if (!supplierId) return null;

   const perCategory = cases
      .filter((supplierCase) => supplierCase?.supplier?.id === supplierId)
      .reduce<Map<string, SummaryCategoryScore>>((acc, supplierCase) => {
         const evaluationState = buildEvaluationState(supplierCase);

         Object.values(evaluationState).forEach((evaluation) => {
            if (evaluation.status !== 'CLOSED' || evaluation.updatedRiskScore === undefined) return;

            const key = normalizeRiskCategoryKey(evaluation.riskCategoryKey);
            if (!key) return;

            const updatedAtMs = toEpochMs(evaluation.updatedAt ?? supplierCase.updatedAt ?? supplierCase.createdAt);
            const previous = acc.get(key);

            if (!previous || updatedAtMs >= previous.updatedAtMs) {
               acc.set(key, {
                  key,
                  value: evaluation.updatedRiskScore,
                  updatedAtMs,
               });
            }
         });

         return acc;
      }, new Map<string, SummaryCategoryScore>());

   if (perCategory.size === 0) return null;

   const riskCategories = Array.from(perCategory.values(), ({ key, value }) => ({ key, value }));
   const overallRiskScore = riskCategories.reduce((sum, entry) => sum + entry.value, 0) / riskCategories.length;

   return {
      overallRiskScore,
      riskCategories: riskCategories.toSorted((left, right) => left.key.localeCompare(right.key)),
   };
};

export const getAuthorLabel = (user?: User | null) => {
   if (!user) return '-';
   const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
   if (fullName) return fullName;
   return user.userName || user.email || '-';
};

export const getQuestionnaireUser = (row: SupplyChainQuestionnaire, key: 'createdBy' | 'updatedBy') => {
   const user = (row as { createdBy?: User | null; updatedBy?: User | null })[key];
   return user ?? null;
};

const resolveLocalizedString = (value: LocalizedString, locale?: string) => {
   if (typeof value === 'string') return value.trim();

   const normalizedLocale = (locale ?? 'en').toLowerCase();
   const baseLocale = normalizedLocale.split('-')[0];
   const normalizedValues = Object.entries(value).reduce<Record<string, string>>((acc, [key, entry]) => {
      acc[key.toLowerCase()] = entry;
      return acc;
   }, {});
   const localized = normalizedValues[normalizedLocale] ?? normalizedValues[baseLocale] ?? normalizedValues.en ?? value.default;

   if (localized?.trim()) return localized.trim();

   const fallback = Object.values(normalizedValues).find((entry) => entry.trim());

   if (fallback?.trim()) return fallback.trim();

   return value.default?.trim() ?? '';
};

const resolveSurveyTitle = (surveyJson: unknown, locale?: string) => {
   const parsed = surveyJsonSchema.safeParse(surveyJson);

   if (!parsed.success) return '';

   const { title } = parsed.data;

   if (!title) return '';

   return resolveLocalizedString(title, locale);
};

export const resolveQuestionnaireDisplayTitle = ({
   intl,
   name,
   surveyJson,
}: {
   intl: Pick<IntlShape, 'formatMessage' | 'locale'>;
   name?: string | null;
   surveyJson: unknown;
}) => {
   const localizedTitle = resolveSurveyTitle(surveyJson, intl.locale);
   if (localizedTitle) return localizedTitle;

   const fallbackName = name?.trim();
   if (fallbackName) return fallbackName;

   return intl.formatMessage({
      id: 'supplyChain.questionnaires.untitled',
      defaultMessage: 'Untitled questionnaire',
   });
};

const getSurveyRuntimeLocale = (locale?: string) => {
   const normalizedLocale = locale?.toLowerCase().split('-')[0];
   if (normalizedLocale === '') return 'en';
   return normalizedLocale ?? 'en';
};

const resolveQuestionText = (value?: string | null): string => {
   if (!value) return '';
   return value.trim();
};

const resolveRuntimeSurveyText = (value: unknown): string => {
   if (typeof value === 'string') return value.trim();
   if (!value || typeof value !== 'object') return '';

   const record = value as Record<string, unknown>;
   const renderedText = typeof record.renderedText === 'string' ? record.renderedText.trim() : '';
   if (renderedText) return renderedText;

   const text = typeof record.text === 'string' ? record.text.trim() : '';
   if (text) return text;

   return '';
};

const resolveSurveyText = (value: unknown, locale?: string): string => {
   if (typeof value === 'string') return value.trim();
   if (typeof value === 'number' || typeof value === 'boolean') return String(value);
   if (!value || typeof value !== 'object' || Array.isArray(value)) return '';

   const record = value as Record<string, unknown>;
   const localized = Object.entries(record).reduce<Record<string, string>>((acc, [key, entry]) => {
      if (typeof entry === 'string') acc[key.toLowerCase()] = entry;
      return acc;
   }, {});

   const normalizedLocale = (locale ?? 'en').toLowerCase();
   const baseLocale = normalizedLocale.split('-')[0];
   return (
      localized[normalizedLocale]?.trim() ||
      localized[baseLocale]?.trim() ||
      localized.en?.trim() ||
      localized.default?.trim() ||
      resolveRuntimeSurveyText(record.locText) ||
      resolveRuntimeSurveyText(record.locTitle) ||
      resolveRuntimeSurveyText(record.text) ||
      resolveRuntimeSurveyText(record.title) ||
      ''
   );
};

const unscorableQuestionTypes = new Set(['image', 'signaturepad']);

const getSurveyQuestionType = (record: Record<string, unknown>) => (typeof record.type === 'string' ? record.type.trim().toLowerCase() : '');

const listSurveyQuestionRiskCategories = (surveyJson: Record<string, unknown>) => {
   const riskCategoryByQuestionName = new Map<string, string>();

   const visit = (value: unknown) => {
      if (Array.isArray(value)) {
         value.forEach(visit);
         return;
      }

      if (!value || typeof value !== 'object') return;

      const record = value as Record<string, unknown>;
      const questionName = typeof record.name === 'string' ? record.name.trim() : '';
      const questionType = getSurveyQuestionType(record);
      const riskCategory = typeof record.riskCategory === 'string' && record.riskCategory.trim() ? record.riskCategory.trim() : '';

      if (questionName && riskCategory && !unscorableQuestionTypes.has(questionType)) {
         riskCategoryByQuestionName.set(questionName, riskCategory);
      }

      Object.values(record).forEach(visit);
   };

   visit(surveyJson);

   return riskCategoryByQuestionName;
};

const findNestedImpactQuestionName = (value: unknown, impactByQuestionName: Record<string, QuestionnaireAnswerImpactEntry>): string | null => {
   if (Array.isArray(value)) {
      for (const entry of value) {
         const questionName = findNestedImpactQuestionName(entry, impactByQuestionName);
         if (questionName) return questionName;
      }

      return null;
   }

   if (!value || typeof value !== 'object') return null;

   const record = value as Record<string, unknown>;
   for (const questionName of Object.keys(record)) {
      if (impactByQuestionName[questionName]) return questionName;
   }

   for (const nestedValue of Object.values(record)) {
      const questionName = findNestedImpactQuestionName(nestedValue, impactByQuestionName);
      if (questionName) return questionName;
   }

   return null;
};

const resolveQuestionnaireAnswerImpact = ({
   impactByQuestionName,
   questionName,
   responseValue,
}: {
   impactByQuestionName: Record<string, QuestionnaireAnswerImpactEntry>;
   questionName: string;
   responseValue: unknown;
}) => {
   if (Object.prototype.hasOwnProperty.call(impactByQuestionName, questionName)) {
      return { impact: impactByQuestionName[questionName], questionName };
   }

   const nestedQuestionName = findNestedImpactQuestionName(responseValue, impactByQuestionName);
   if (!nestedQuestionName) return { impact: null, questionName };

   return { impact: impactByQuestionName[nestedQuestionName] ?? null, questionName: nestedQuestionName };
};

const buildMatrixRowImpactKey = (questionName: string, rowValue: string) => JSON.stringify([questionName, rowValue]);

const resolveDisplayValueText = (value: unknown): string => {
   if (value === null || value === undefined) return '';
   if (typeof value === 'string') return value.trim();
   if (typeof value === 'number' || typeof value === 'boolean') return String(value);
   if (value instanceof Date) return value.toISOString();
   return '';
};

const answerCommentSuffix = '-Comment';

const isAnswerCommentKey = (key: string) => key.endsWith(answerCommentSuffix);

const hasFileShape = (value: unknown): value is { content?: unknown; name?: unknown; type?: unknown } => {
   if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
   return 'name' in value || 'type' in value || 'content' in value;
};

const resolveFileName = (value: { name?: unknown }, intl: IntlShape, index: number) => {
   if (typeof value.name === 'string' && value.name.trim()) return value.name.trim();

   return intl.formatMessage(
      {
         id: 'supplyChain.questionnaires.detail.fileFallback',
         defaultMessage: 'File {index}',
      },
      { index: index + 1 }
   );
};

interface QuestionnaireAnswerAttachment {
   id: number;
   name: string;
   mimeType: string | null;
   sizeBytes: number | null;
   url: string | null;
   questionKey: string | null;
}

const findQuestionnaireAnswerAttachment = ({
   attachments,
   fileName,
   index,
   questionKey,
}: {
   attachments: QuestionnaireAnswerAttachment[];
   fileName: string;
   index: number;
   questionKey: string;
}) => {
   const matchingQuestionAttachments = attachments.filter((attachment) => attachment.questionKey === questionKey);
   const matchingQuestionAndName = matchingQuestionAttachments.find((attachment) => attachment.name === fileName);
   if (matchingQuestionAndName) return matchingQuestionAndName;

   const matchingQuestionByIndex = matchingQuestionAttachments[index];
   if (matchingQuestionByIndex) return matchingQuestionByIndex;

   return attachments.find((attachment) => attachment.name === fileName) ?? null;
};

const buildQuestionnaireAnswerFile = ({
   attachments,
   entry,
   index,
   intl,
   questionKey,
}: {
   attachments: QuestionnaireAnswerAttachment[];
   entry: { name?: unknown; type?: unknown };
   index: number;
   intl: IntlShape;
   questionKey: string;
}) => {
   const name = resolveFileName(entry, intl, index);
   const attachment = findQuestionnaireAnswerAttachment({
      attachments,
      fileName: name,
      index,
      questionKey,
   });

   return {
      id: attachment?.id,
      name,
      type: typeof entry.type === 'string' && entry.type.trim() ? entry.type.trim() : attachment?.mimeType,
      mimeType: attachment?.mimeType,
      sizeBytes: attachment?.sizeBytes,
      url: attachment?.url,
      questionKey: attachment?.questionKey ?? questionKey,
   };
};

const listQuestionnaireAnswerFiles = ({
   attachments,
   intl,
   questionKey,
   value,
}: {
   attachments: QuestionnaireAnswerAttachment[];
   intl: IntlShape;
   questionKey: string;
   value: unknown;
}) => {
   if (hasFileShape(value)) {
      return [
         buildQuestionnaireAnswerFile({
            attachments,
            entry: value,
            index: 0,
            intl,
            questionKey,
         }),
      ];
   }

   if (!Array.isArray(value)) return [];

   const fileEntries = value.filter(hasFileShape);
   if (fileEntries.length !== value.length) return [];

   return fileEntries.map((entry, index) =>
      buildQuestionnaireAnswerFile({
         attachments,
         entry,
         index,
         intl,
         questionKey,
      })
   );
};

const isSingleNestedObjectArray = (value: unknown[]): value is [Record<string, unknown>] =>
   value.length === 1 && Boolean(value[0]) && typeof value[0] === 'object' && !hasFileShape(value[0]);

type SurveyAnswerChoiceMetadata = {
   value: string;
   label: string;
};

type SurveyAnswerColumnMetadata = SurveyAnswerChoiceMetadata & {
   choices: Map<string, string>;
};

type SurveyQuestionAnswerMetadata = {
   type: string;
   questionTitle: string;
   rows: SurveyAnswerChoiceMetadata[];
   columns: SurveyAnswerColumnMetadata[];
   choices: Map<string, string>;
   fixedPanelChildren: string[];
   sectionId?: string | null;
   sectionTitle?: string | null;
};

const resolveSurveyItemValue = (value: unknown) => {
   if (typeof value === 'string') return value.trim();
   if (typeof value === 'number' || typeof value === 'boolean') return String(value);
   if (!value || typeof value !== 'object' || Array.isArray(value)) return '';

   const record = value as Record<string, unknown>;
   return (
      resolveDisplayValueText(record.value) || resolveSurveyText(record.name) || resolveSurveyText(record.text) || resolveSurveyText(record.title)
   );
};

const resolveSurveyItemLabel = (value: unknown, intl: IntlShape) => {
   const fallbackValue = resolveSurveyItemValue(value);
   if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return fallbackValue;
   if (!value || typeof value !== 'object' || Array.isArray(value)) return fallbackValue;

   const record = value as Record<string, unknown>;
   return (
      resolveSurveyText(record.text, intl.locale) ||
      resolveSurveyText(record.title, intl.locale) ||
      resolveSurveyText(record.name, intl.locale) ||
      fallbackValue
   );
};

const listSurveyItems = (value: unknown, intl: IntlShape): SurveyAnswerChoiceMetadata[] => {
   if (!Array.isArray(value)) return [];

   return value
      .map((entry) => {
         const itemValue = resolveSurveyItemValue(entry);
         if (!itemValue) return null;

         return {
            value: itemValue,
            label: resolveSurveyItemLabel(entry, intl) || itemValue,
         };
      })
      .filter((entry): entry is SurveyAnswerChoiceMetadata => Boolean(entry));
};

const buildChoiceLabelMap = (items: SurveyAnswerChoiceMetadata[]) => new Map(items.map((item) => [item.value, item.label]));

const listSurveyQuestionNames = (value: unknown): string[] => {
   const questionNames: string[] = [];

   const visit = (entry: unknown) => {
      if (Array.isArray(entry)) {
         entry.forEach(visit);
         return;
      }

      if (!entry || typeof entry !== 'object') return;

      const record = entry as Record<string, unknown>;
      const questionName = typeof record.name === 'string' ? record.name.trim() : '';
      const questionType = getSurveyQuestionType(record);

      if (questionName && questionType !== 'panel' && questionType !== 'paneldynamic') questionNames.push(questionName);

      if (Array.isArray(record.elements)) visit(record.elements);
      if (Array.isArray(record.templateElements)) visit(record.templateElements);
   };

   visit(value);

   return questionNames;
};

const buildSurveyQuestionAnswerMetadata = (surveyJson: Record<string, unknown>, intl: IntlShape) => {
   const metadataByQuestionName = new Map<string, SurveyQuestionAnswerMetadata>();

   const visit = (
      value: unknown,
      context: {
         sectionId?: string | null;
         sectionTitle?: string | null;
      } = {}
   ) => {
      if (Array.isArray(value)) {
         value.forEach((entry) => visit(entry, context));
         return;
      }

      if (!value || typeof value !== 'object') return;

      const record = value as Record<string, unknown>;
      const questionType = getSurveyQuestionType(record);
      const questionName = typeof record.name === 'string' ? record.name.trim() : '';
      const questionTitle = resolveSurveyText(record.title, intl.locale) || questionName;
      const isFixedSinglePanel =
         questionType === 'paneldynamic' &&
         record.panelCount === 1 &&
         record.allowAddPanel === false &&
         record.allowRemovePanel === false &&
         Array.isArray(record.templateElements);
      const isPanel = questionType === 'panel' && Array.isArray(record.elements);

      if (questionName) {
         const rows = listSurveyItems(record.rows, intl);
         const rawColumns = Array.isArray(record.columns) ? record.columns : [];
         const columns = listSurveyItems(record.columns, intl).map((column) => {
            const columnRecord = rawColumns.find((entry) => resolveSurveyItemValue(entry) === column.value);
            const columnChoices =
               columnRecord && typeof columnRecord === 'object' && !Array.isArray(columnRecord)
                  ? listSurveyItems((columnRecord as Record<string, unknown>).choices, intl)
                  : [];

            return {
               ...column,
               choices: buildChoiceLabelMap(columnChoices),
            };
         });
         const choices = buildChoiceLabelMap(listSurveyItems(record.choices, intl));

         metadataByQuestionName.set(questionName, {
            type: questionType,
            questionTitle,
            rows,
            columns,
            choices,
            fixedPanelChildren: isFixedSinglePanel ? listSurveyQuestionNames(record.templateElements) : [],
            sectionId: context.sectionId,
            sectionTitle: context.sectionTitle,
         });
      }

      if (isFixedSinglePanel) {
         const sectionTitle = questionTitle || context.sectionTitle;
         const sectionId = questionName || context.sectionId;
         visit(record.templateElements, {
            sectionId,
            sectionTitle,
         });
         return;
      }

      if (isPanel) {
         visit(record.elements, {
            sectionId: questionName || context.sectionId,
            sectionTitle: questionTitle || context.sectionTitle,
         });
         return;
      }

      Object.values(record).forEach((entry) => visit(entry, context));
   };

   visit(surveyJson);
   return metadataByQuestionName;
};

const resolveNestedAnswerText = (value: unknown, intl: IntlShape): string => {
   const scalarText = resolveDisplayValueText(value);
   if (scalarText) return scalarText;

   if (hasFileShape(value)) return resolveFileName(value, intl, 0);

   if (Array.isArray(value)) {
      const fileEntries = value.filter(hasFileShape);
      if (fileEntries.length === value.length && fileEntries.length > 0) {
         return fileEntries.map((entry, index) => resolveFileName(entry, intl, index)).join(', ');
      }

      return value
         .map((entry) => resolveNestedAnswerText(entry, intl))
         .filter(Boolean)
         .join(', ');
   }

   if (!value || typeof value !== 'object') return '';

   return Object.entries(value)
      .filter(([label]) => !isAnswerCommentKey(label))
      .map(([label, entry]) => {
         const entryText = resolveNestedAnswerText(entry, intl);
         if (!entryText) return label.trim();
         if (!label.trim()) return entryText;
         return `${label}: ${entryText}`;
      })
      .filter(Boolean)
      .join(', ');
};

const listStoredAnswerComments = (value: unknown, intl: IntlShape, questionName?: string): string[] => {
   if (Array.isArray(value)) {
      return value.flatMap((entry) => listStoredAnswerComments(entry, intl, questionName));
   }

   if (!value || typeof value !== 'object' || hasFileShape(value)) return [];

   return Object.entries(value).flatMap(([label, entry]) => {
      if (isAnswerCommentKey(label)) {
         if (questionName && label !== `${questionName}${answerCommentSuffix}`) return [];
         const comment = resolveNestedAnswerText(entry, intl).trim();
         return comment ? [comment] : [];
      }

      return listStoredAnswerComments(entry, intl, questionName);
   });
};

const resolveAnswerComment = ({
   fallbackValue,
   intl,
   questionComment,
   questionName,
   responseData,
}: {
   fallbackValue: unknown;
   intl: IntlShape;
   questionComment: unknown;
   questionName: string;
   responseData: Record<string, unknown>;
}) => {
   const surveyComment = resolveNestedAnswerText(questionComment, intl).trim();
   if (surveyComment) return surveyComment;

   const storedComments = [...listStoredAnswerComments(responseData, intl, questionName), ...listStoredAnswerComments(fallbackValue, intl)].filter(
      Boolean
   );

   return Array.from(new Set(storedComments)).join('\n').trim();
};

const parseQuestionnaireAnswerDisplay = (
   value: unknown,
   intl: IntlShape,
   questionKey: string,
   attachments: QuestionnaireAnswerAttachment[],
   impactByQuestionName: Record<string, QuestionnaireAnswerImpactEntry>,
   metadata?: SurveyQuestionAnswerMetadata
): Pick<
   QuestionnaireAnswerRow,
   'answerEntries' | 'answerFiles' | 'answerItems' | 'answerKind' | 'answerText' | 'matrixDropdownRows' | 'matrixRows'
> => {
   if (value === null || value === undefined) {
      return {
         answerKind: 'empty',
         answerText: '',
      };
   }

   const scalarText = resolveDisplayValueText(value);
   if (scalarText) {
      return {
         answerKind: 'text',
         answerText: scalarText,
      };
   }

   if (metadata?.type === 'matrix' && value && typeof value === 'object' && !Array.isArray(value)) {
      const rowLabels = buildChoiceLabelMap(metadata.rows);
      const columnLabels = new Map(metadata.columns.map((column) => [column.value, column.label]));
      const rawEntries = Object.entries(value).filter(([rowValue]) => !isAnswerCommentKey(rowValue));
      const metadataRowValues = new Set(metadata.rows.map((row) => row.value));
      type MatrixAnswerDisplayRow = NonNullable<QuestionnaireAnswerRow['matrixRows']>[number];
      const orderedEntries =
         metadata.rows.length > 0
            ? [
                 ...metadata.rows
                    .map((row) => [row.value, (value as Record<string, unknown>)[row.value]] as const)
                    .filter(([, answer]) => answer !== undefined),
                 ...rawEntries.filter(([rowValue]) => !metadataRowValues.has(rowValue)),
              ]
            : rawEntries;
      const matrixRows: MatrixAnswerDisplayRow[] = orderedEntries
         .map(([rowValue, answerValue]): MatrixAnswerDisplayRow | null => {
            const rowQuestionName = buildMatrixRowImpactKey(questionKey, rowValue);
            const rowImpact = impactByQuestionName[rowQuestionName];
            const rowRiskCategory = rowImpact?.impactCategory ?? null;
            const normalizedAnswer = resolveDisplayValueText(answerValue);
            const answerLabel = normalizedAnswer
               ? (columnLabels.get(normalizedAnswer) ?? normalizedAnswer)
               : resolveNestedAnswerText(answerValue, intl);
            const statement = rowLabels.get(rowValue) ?? rowValue;
            if (!statement.trim() && !answerLabel.trim()) return null;

            return {
               questionName: rowQuestionName,
               statement,
               answer: answerLabel,
               impactScore: typeof rowImpact?.impactScore === 'number' ? rowImpact.impactScore : null,
               impactCategory: rowImpact?.impactCategory ?? null,
               riskCategory: rowRiskCategory,
               riskCategoryLabel: rowRiskCategory ? resolveRiskCategoryLabel(intl, rowRiskCategory) : undefined,
            };
         })
         .filter((entry): entry is MatrixAnswerDisplayRow => Boolean(entry));

      if (matrixRows.length > 0) {
         return {
            answerKind: 'matrix',
            matrixRows,
            answerText: matrixRows.map((row) => `${row.statement}: ${row.answer}`).join(', '),
         };
      }
   }

   if (metadata?.type === 'matrixdropdown' && value && typeof value === 'object' && !Array.isArray(value)) {
      const rowLabels = buildChoiceLabelMap(metadata.rows);
      const rawEntries = Object.entries(value).filter(([rowValue]) => !isAnswerCommentKey(rowValue));
      const metadataRowValues = new Set(metadata.rows.map((row) => row.value));
      const orderedEntries =
         metadata.rows.length > 0
            ? [
                 ...metadata.rows
                    .map((row) => [row.value, (value as Record<string, unknown>)[row.value]] as const)
                    .filter(([, answer]) => answer !== undefined),
                 ...rawEntries.filter(([rowValue]) => !metadataRowValues.has(rowValue)),
              ]
            : rawEntries;
      const matrixDropdownRows = orderedEntries
         .map(([rowValue, rowAnswer]) => {
            if (!rowAnswer || typeof rowAnswer !== 'object' || Array.isArray(rowAnswer)) return null;

            const rowAnswerRecord = rowAnswer as Record<string, unknown>;
            const metadataColumnsByValue = new Map(metadata.columns.map((column) => [column.value, column]));
            const orderedColumns = [
               ...metadata.columns.map((column) => [column.value, column] as const),
               ...Object.keys(rowAnswerRecord)
                  .filter((columnValue) => !metadataColumnsByValue.has(columnValue))
                  .map((columnValue) => [columnValue, null] as const),
            ];
            const answers = orderedColumns
               .map(([columnValue, column]) => {
                  const rawAnswer = rowAnswerRecord[columnValue];
                  const normalizedAnswer = resolveDisplayValueText(rawAnswer);
                  const valueLabel = normalizedAnswer
                     ? (column?.choices.get(normalizedAnswer) ?? normalizedAnswer)
                     : resolveNestedAnswerText(rawAnswer, intl);

                  return {
                     label: column?.label ?? columnValue,
                     value: valueLabel,
                  };
               })
               .filter((answer) => answer.label.trim() || answer.value.trim());

            if (answers.length === 0) return null;

            return {
               statement: rowLabels.get(rowValue) ?? rowValue,
               answers,
            };
         })
         .filter((entry): entry is { statement: string; answers: Array<{ label: string; value: string }> } => Boolean(entry));

      if (matrixDropdownRows.length > 0) {
         return {
            answerKind: 'matrix_dropdown',
            matrixDropdownRows,
            answerText: matrixDropdownRows
               .map((row) => `${row.statement}: ${row.answers.map((answer) => `${answer.label}: ${answer.value}`).join(', ')}`)
               .join(', '),
         };
      }
   }

   if (Array.isArray(value)) {
      const fileEntries = value.filter(hasFileShape);
      if (fileEntries.length === value.length && fileEntries.length > 0) {
         const answerFiles = fileEntries.map((entry, index) =>
            buildQuestionnaireAnswerFile({
               attachments,
               entry,
               index,
               intl,
               questionKey,
            })
         );

         return {
            answerKind: 'files',
            answerFiles,
            answerText: answerFiles.map(({ name }) => name).join(', '),
         };
      }

      if (isSingleNestedObjectArray(value)) {
         return parseQuestionnaireAnswerDisplay(value[0], intl, questionKey, attachments, impactByQuestionName, metadata);
      }

      const answerItems = value.map((entry) => resolveNestedAnswerText(entry, intl)).filter(Boolean);
      if (answerItems.length > 0) {
         return {
            answerKind: 'list',
            answerItems,
            answerText: answerItems.join(', '),
         };
      }

      return {
         answerKind: 'empty',
         answerText: '',
      };
   }

   if (hasFileShape(value)) {
      const answerFiles = [
         buildQuestionnaireAnswerFile({
            attachments,
            entry: value,
            index: 0,
            intl,
            questionKey,
         }),
      ];

      return {
         answerKind: 'files',
         answerFiles,
         answerText: answerFiles[0]?.name ?? '',
      };
   }

   if (typeof value === 'object') {
      const answerEntries = Object.entries(value)
         .filter(([label]) => !isAnswerCommentKey(label))
         .map(([label, entry]) => {
            const files = listQuestionnaireAnswerFiles({
               attachments,
               intl,
               questionKey,
               value: entry,
            });
            const entryValue = files.length > 0 ? files.map(({ name }) => name).join(', ') : resolveNestedAnswerText(entry, intl);

            return {
               label,
               value: entryValue,
               files: files.length > 0 ? files : undefined,
            };
         })
         .filter(({ label, value: entryValue, files }) => label.trim() || entryValue.trim() || Boolean(files?.length));

      if (answerEntries.length > 0) {
         return {
            answerKind: 'key_value_list',
            answerEntries,
            answerText: answerEntries.map(({ label, value: entryValue }) => `${label}: ${entryValue}`).join(', '),
         };
      }
   }

   return {
      answerKind: 'empty',
      answerText: '',
   };
};

export const buildQuestionnaireAnswerRows = ({
   surveyJson,
   responseData,
   impactByQuestionName,
   intl,
   attachments = [],
}: {
   surveyJson: Record<string, unknown>;
   responseData: Record<string, unknown>;
   impactByQuestionName: Record<string, QuestionnaireAnswerImpactEntry>;
   intl: IntlShape;
   attachments?: QuestionnaireAnswerAttachment[];
}): QuestionnaireAnswerRow[] => {
   let model: Model;
   try {
      model = new Model(surveyJson);
      model.locale = getSurveyRuntimeLocale(intl.locale);
   } catch {
      return [];
   }

   model.data = responseData ?? {};
   const riskCategoryByQuestionName = listSurveyQuestionRiskCategories(surveyJson);
   const metadataByQuestionName = buildSurveyQuestionAnswerMetadata(surveyJson, intl);

   const fallbackSection = intl.formatMessage({
      id: 'supplyChain.questionnaires.detail.sectionFallback',
      defaultMessage: 'General',
   });

   const buildRow = ({
      answerValue,
      index,
      pageId,
      pageTitle,
      questionName,
      questionTitle,
      responseValue,
   }: {
      answerValue: unknown;
      index: number;
      pageId: string;
      pageTitle: string;
      questionName: string;
      questionTitle: string;
      responseValue: unknown;
   }): QuestionnaireAnswerRow => {
      const metadata = metadataByQuestionName.get(questionName);
      const answerDisplay = parseQuestionnaireAnswerDisplay(answerValue, intl, questionName, attachments, impactByQuestionName, metadata);
      const resolvedImpact = resolveQuestionnaireAnswerImpact({
         impactByQuestionName,
         questionName,
         responseValue,
      });
      const impact = resolvedImpact.impact;
      const impactScore = typeof impact?.impactScore === 'number' ? impact.impactScore : null;
      const impactCategory = impact?.impactCategory ?? null;
      const riskCategory = riskCategoryByQuestionName.get(questionName) ?? riskCategoryByQuestionName.get(resolvedImpact.questionName) ?? null;
      const riskCategoryLabel = riskCategory ? resolveRiskCategoryLabel(intl, riskCategory) : fallbackSection;

      return {
         id: `${questionName}-${index}`,
         questionName: resolvedImpact.questionName,
         question: questionTitle,
         pageId,
         pageTitle,
         questionOrder: index,
         ...answerDisplay,
         impactScore,
         impactCategory,
         riskCategory,
         riskCategoryLabel,
      };
   };

   return model.getAllQuestions().flatMap((question, index) => {
      const name = question.name || `question-${index}`;
      const questionRecord = question as unknown as Record<string, unknown>;
      const resolvedTitle = resolveRuntimeSurveyText(questionRecord.locTitle) || resolveQuestionText(question.title);
      const title = resolvedTitle.length > 0 ? resolvedTitle : name;
      const page = question.page;
      const pageRecord = page ? (page as unknown as Record<string, unknown>) : null;
      const pageIndex = page ? model.pages.findIndex((candidatePage) => candidatePage === page) : -1;
      const pageNumber = pageIndex >= 0 ? pageIndex + 1 : null;
      const pageTitle =
         resolveRuntimeSurveyText(pageRecord?.locTitle) ||
         resolveRuntimeSurveyText(pageRecord?.title) ||
         (pageNumber
            ? intl.formatMessage(
                 {
                    id: 'supplyChain.questionnaires.detail.pageFallback',
                    defaultMessage: 'Page {index}',
                 },
                 { index: pageNumber }
              )
            : fallbackSection);
      const pageId = typeof page?.name === 'string' && page.name.trim() ? page.name.trim() : pageNumber ? `page-${pageNumber}` : 'questionnaire';
      const metadata = metadataByQuestionName.get(name);
      const sectionTitle = metadata?.sectionTitle?.trim();
      const sectionId = metadata?.sectionId?.trim();
      const rawAnswer = question.displayValue ?? question.value;
      const metadataAnswer = metadata?.type === 'matrix' || metadata?.type === 'matrixdropdown' ? question.value : rawAnswer;
      const resolvedPageId = sectionId && sectionId.length > 0 ? sectionId : pageId;
      const resolvedPageTitle = sectionTitle && sectionTitle.length > 0 ? sectionTitle : pageTitle;

      if (
         metadata?.type === 'paneldynamic' &&
         metadata.fixedPanelChildren.length > 0 &&
         Array.isArray(question.value) &&
         isSingleNestedObjectArray(question.value)
      ) {
         const panelAnswer = question.value[0];
         const childRows = metadata.fixedPanelChildren
            .map((childQuestionName, childIndex): QuestionnaireAnswerRow | null => {
               const childMetadata = metadataByQuestionName.get(childQuestionName);
               const childAnswer = panelAnswer[childQuestionName];
               if (childAnswer === undefined) return null;

               const row = buildRow({
                  answerValue: childAnswer,
                  index: index + childIndex / 100,
                  pageId: name,
                  pageTitle: metadata.questionTitle.length > 0 ? metadata.questionTitle : resolvedPageTitle,
                  questionName: childQuestionName,
                  questionTitle:
                     childMetadata?.questionTitle && childMetadata.questionTitle.length > 0 ? childMetadata.questionTitle : childQuestionName,
                  responseValue: childAnswer,
               });

               const childAnswerComment = resolveAnswerComment({
                  fallbackValue: childAnswer,
                  intl,
                  questionComment: undefined,
                  questionName: childQuestionName,
                  responseData: panelAnswer,
               });

               if (!childAnswerComment) return row;

               return {
                  ...row,
                  answerComment: childAnswerComment,
               };
            })
            .filter((entry): entry is QuestionnaireAnswerRow => Boolean(entry));

         if (childRows.length > 0) return childRows;
      }

      const answerComment = resolveAnswerComment({
         fallbackValue: metadataAnswer,
         intl,
         questionComment: question.comment,
         questionName: name,
         responseData,
      });
      const row = buildRow({
         answerValue: metadataAnswer,
         index,
         pageId: resolvedPageId,
         pageTitle: resolvedPageTitle,
         questionName: name,
         questionTitle: title,
         responseValue: question.value ?? rawAnswer,
      });

      if (!answerComment) return [row];

      return [
         {
            ...row,
            answerComment,
         },
      ];
   });
};

export const getSupplierLabel = (supplier: SupplierOption) => {
   if (supplier?.name) return supplier.name;
   return '-';
};

const _getSupplierContact = (supplier: SupplierOption) => {
   const contacts = Array.isArray(supplier.contactPeople) ? supplier.contactPeople : [];
   const hasValue = (value?: string | null) => Boolean(value?.trim());
   const primaryContact = contacts.find((contact) => hasValue(contact?.email));

   if (!primaryContact) return null;

   const contactName = [primaryContact.firstName, primaryContact.lastName].filter(Boolean).join(' ');
   const contactEmail = primaryContact.email?.trim() ?? '';

   return {
      name: contactName,
      email: contactEmail,
   };
};

export const getSupplierSecondaryLabel = (supplier: SupplierOption, emptyLabel: string) => {
   const contact = _getSupplierContact(supplier);

   if (!contact) return emptyLabel;

   const parts = [contact.name, contact.email].filter(Boolean);
   return parts.length > 0 ? parts.join(' • ') : emptyLabel;
};

const compareQuestionnaireOverviewDates = (left: Date | null, right: Date | null): number => {
   if (!left && !right) return 0;
   if (!left) return 1;
   if (!right) return -1;
   return left.getTime() - right.getTime();
};

const compareQuestionnaireOverviewText = (left: string, right: string): number => left.localeCompare(right);

const questionnaireOverviewStatusSortOrder: Record<QuestionnaireOverviewStatus, number> = {
   SENT: 0,
   DRAFT: 1,
   REQUESTED: 2,
   REJECTED: 3,
   REVIEW_PENDING: 4,
   COMPLETED: 5,
   EXPIRED: 6,
};

export const compareQuestionnaireOverviewStatuses = (left: QuestionnaireOverviewStatus, right: QuestionnaireOverviewStatus): number => {
   const leftOrder = questionnaireOverviewStatusSortOrder[left] ?? Number.MAX_SAFE_INTEGER;
   const rightOrder = questionnaireOverviewStatusSortOrder[right] ?? Number.MAX_SAFE_INTEGER;
   return leftOrder - rightOrder;
};

export const sortQuestionnaireOverviewRows = <Row extends { dueDate: Date | null; status: QuestionnaireOverviewStatus; name: string }>(
   rows: Row[]
): Row[] =>
   Array.from(rows).sort((left, right) => {
      const dueComparison = compareQuestionnaireOverviewDates(left.dueDate, right.dueDate);
      if (dueComparison !== 0) return dueComparison;
      const statusComparison = compareQuestionnaireOverviewStatuses(left.status, right.status);
      if (statusComparison !== 0) return statusComparison;
      return compareQuestionnaireOverviewText(left.name, right.name);
   });

function _findActiveValidityPeriod(
   supplierOrganisations: Supplier['organisations'] | undefined,
   organisationId: number | null | undefined
): Partial<SupplierOrganisationPeriod> | null {
   if (!organisationId || !supplierOrganisations) return null;

   const today = startOfDay(new Date());

   const match = supplierOrganisations.find((organisation) => {
      if (organisation.id !== organisationId) return false;

      const from = startOfDay(parseISO(organisation.supplier_organisations.validFrom));

      if (from > today) return false;

      if (organisation.supplier_organisations.validTo === null) return true;

      const to = startOfDay(parseISO(organisation.supplier_organisations.validTo));

      return to >= today;
   });

   if (match) {
      return {
         id: match.supplier_organisations.id,
         organisationId: match.supplier_organisations.organisationId,
         supplierId: match.supplier_organisations.supplierId,
         validFrom: match.supplier_organisations.validFrom,
         validTo: match.supplier_organisations.validTo,
      };
   }

   return null;
}

export function isSupplierExpiredForOrg(supplier: Supplier, organisationId: number | null | undefined): boolean {
   const activePeriod = _findActiveValidityPeriod(supplier.organisations, organisationId);
   return activePeriod === null;
}

type NaceProposalRow = { isNaceAiGenerated?: boolean | null; naceProposalReviewedAt?: string | null; supplier?: NaceProposalRow | null };

/**
 * Review status of a supplier's AI-generated NACE proposal, or `null` when the NACE code wasn't AI-generated.
 * `pending` = AI-generated and not yet reviewed; `approved` = AI-generated and reviewed.
 * Accepts either a bare supplier or a subsidiaries-wrapped grid row (reads `row.supplier`).
 */
export function getNaceProposalStatus(row: NaceProposalRow | null | undefined): 'pending' | 'approved' | null {
   const supplier = row?.supplier ?? row;
   if (!supplier?.isNaceAiGenerated) return null;
   return supplier.naceProposalReviewedAt ? 'approved' : 'pending';
}

/** True when the row carries an AI NACE proposal still awaiting review. */
export const isPendingNaceProposal = (row: NaceProposalRow | null | undefined): boolean => getNaceProposalStatus(row) === 'pending';

export const isSupplierRiskKey = (value: string): value is keyof typeof SupplierRisksIcons => value in SupplierRisksIcons;

export type RiskCategoryGroup = 'environment' | 'governance' | 'human_rights';

export const RiskCategoryToRiskCategoryGroup: Record<string, RiskCategoryGroup> = {
   CHILD_LABOR: 'human_rights',
   FORCED_LABOR: 'human_rights',
   WORKING_CONDITIONS: 'human_rights',
   COLLECTIVE_BARGAINING: 'human_rights',
   DISCRIMINATION: 'human_rights',
   LAND_RIGHTS: 'human_rights',
   FUNDAMENTAL_RIGHTS: 'human_rights',

   POLLUTION: 'environment',
   DEFORESTATION_BIODIVERSITY: 'environment',
   WASTE: 'environment',
   WATER_CONSUMPTION: 'environment',

   CORRUPTION: 'governance',
   POLITICAL_STABILITY: 'governance',
};

export function priorityEnumToNumericValue(priority: SupplyChainPriorityEnum) {
   let value;
   switch (priority) {
      case SupplyChainPriority.LOW:
         value = 1;
         break;
      case SupplyChainPriority.MEDIUM:
         value = 2;
         break;
      case SupplyChainPriority.HIGH:
         value = 3;
         break;
      default:
         value = 0;
   }
   return value;
}
